import React, { useState } from "react";
import { useAuth } from "../../lib/auth";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, Pencil, Check, X, Mail, KeyRound } from "lucide-react";

function Row({ label, value, action }) {
  return (
    <div className="flex justify-between items-center py-2 border-b border-[#1a3c5e]/10 last:border-0">
      <span className="text-gray-500">{label}</span>
      <div className="flex items-center gap-2">
        <span className="font-semibold text-[#1a3c5e]">{value}</span>
        {action}
      </div>
    </div>
  );
}

function EditableRow({ label, value, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await onSave(draft.trim());
      setEditing(false);
      toast.success(`${label} updated`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <Row
        label={label}
        value={value || "—"}
        action={
          <button onClick={() => { setDraft(value || ""); setEditing(true); }} data-testid={`edit-${label.toLowerCase()}`} className="text-gray-400 hover:text-[#F7941D]">
            <Pencil className="w-3.5 h-3.5" />
          </button>
        }
      />
    );
  }
  return (
    <div className="flex justify-between items-center py-2 border-b border-[#1a3c5e]/10 last:border-0 gap-2">
      <span className="text-gray-500 shrink-0">{label}</span>
      <div className="flex items-center gap-1.5 flex-1 justify-end">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          data-testid={`input-${label.toLowerCase()}`}
          className="border border-gray-300 rounded-lg px-2 py-1 text-sm w-40 focus:outline-none focus:ring-2 focus:ring-[#F7941D]"
          autoFocus
        />
        <button onClick={save} disabled={saving} data-testid={`save-${label.toLowerCase()}`} className="text-emerald-600 hover:text-emerald-700">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
        </button>
        <button onClick={() => setEditing(false)} disabled={saving} className="text-gray-400 hover:text-gray-600">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function SetEmailPasswordCard({ onDone }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/me/set-email-password", { email, password });
      toast.success("Email & password added — you can now log in either way.");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to add email/password");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-4 p-4 rounded-xl bg-[#FEF3E2]/60 border border-[#F7941D]/30">
      <div className="flex items-center gap-2 mb-2">
        <Mail className="w-4 h-4 text-[#F7941D]" />
        <h3 className="font-semibold text-[#1a3c5e] text-sm">Add email & password</h3>
      </div>
      <p className="text-xs text-gray-600 mb-3">
        You signed up with just your phone number. Add an email + password so you can also log in that way, recover access if you lose your phone, and receive email receipts.
      </p>
      <form onSubmit={submit} className="space-y-2">
        <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="set-email-input" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#F7941D]" />
        <input type="password" placeholder="Password (min 6 characters)" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} data-testid="set-password-input" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#F7941D]" />
        <button disabled={submitting} type="submit" data-testid="set-email-password-submit" className="w-full py-2 bg-[#F7941D] hover:bg-[#e0861a] disabled:opacity-60 text-white text-sm font-semibold rounded-lg flex items-center justify-center gap-2">
          {submitting && <Loader2 className="w-4 h-4 animate-spin" />} Save
        </button>
      </form>
    </div>
  );
}

function ChangePasswordCard() {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/me/change-password", { current_password: current, new_password: next });
      toast.success("Password changed");
      setOpen(false); setCurrent(""); setNext("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to change password");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} data-testid="change-password-toggle" className="mt-4 text-sm font-semibold text-[#F7941D] hover:underline flex items-center gap-1.5">
        <KeyRound className="w-3.5 h-3.5" /> Change password
      </button>
    );
  }
  return (
    <form onSubmit={submit} className="mt-4 p-4 rounded-xl bg-[#FEF3E2]/60 border border-[#1a3c5e]/10 space-y-2">
      <input type="password" placeholder="Current password" value={current} onChange={(e) => setCurrent(e.target.value)} required data-testid="current-password-input" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#F7941D]" />
      <input type="password" placeholder="New password (min 6 characters)" value={next} onChange={(e) => setNext(e.target.value)} required minLength={6} data-testid="new-password-input" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#F7941D]" />
      <div className="flex gap-2">
        <button disabled={submitting} type="submit" data-testid="change-password-submit" className="flex-1 py-2 bg-[#F7941D] hover:bg-[#e0861a] disabled:opacity-60 text-white text-sm font-semibold rounded-lg flex items-center justify-center gap-2">
          {submitting && <Loader2 className="w-4 h-4 animate-spin" />} Save
        </button>
        <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 border border-gray-300 text-gray-600 text-sm font-semibold rounded-lg">Cancel</button>
      </div>
    </form>
  );
}

export default function Account() {
  const { user, refresh } = useAuth();

  const saveField = async (field, value) => {
    await api.put("/me/profile", { [field]: value });
    await refresh();
  };

  return (
    <div className="bg-white rounded-2xl border border-[#1a3c5e]/10 p-6">
      <h2 className="font-display text-2xl font-bold text-[#1a3c5e] mb-4">Account Details</h2>
      <div className="space-y-1 text-sm">
        <EditableRow label="Name" value={user?.name} onSave={(v) => saveField("name", v)} />
        <Row label="Email" value={user?.email || "—"} />
        <Row label="Phone" value={user?.phone || "—"} />
        <EditableRow label="City" value={user?.city} onSave={(v) => saveField("city", v)} />
        <Row label="Role" value={user?.role?.toUpperCase()} />
        <Row label="Member since" value={user?.created_at ? new Date(user.created_at).toLocaleDateString("en-IN") : "—"} />
      </div>

      {!user?.email && <SetEmailPasswordCard onDone={refresh} />}
      {user?.email && <ChangePasswordCard />}
    </div>
  );
}
